import { useState } from "react";

import {
  applySecondaryCorrection,
  formatTime,
  parseTime,
  type PortPrediction,
  type SecondaryDifferences,
} from "@nav-games/tides";

/// Secondary-port calculator — the Almanac arithmetic that takes a
/// Standard Port prediction (HW + LW times and heights) and applies
/// the secondary-port differences from the Almanac's tabulated tables
/// to produce the secondary port's HW + LW.
///
/// In real life the difference values vary linearly between the
/// "MHWS / MHWN / MLWN / MLWS" columns based on the height of HW + LW
/// at the standard port on the day. This calculator does the simpler
/// "direct addition" form first (which the RYA Day Skipper exam
/// often accepts), then shows a note about range-interpolation for
/// students working toward Coastal Skipper / Yachtmaster Coastal.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  hint,
  step = 0.1,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  step?: number;
  type?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flex: 1,
        minWidth: 120,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type={type}
        inputMode={type === "number" ? "decimal" : "text"}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function fmtMinDiff(mins: number): string {
  const sign = mins >= 0 ? "+" : "−";
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return h > 0
    ? `${sign}${h}h ${String(m).padStart(2, "0")}m`
    : `${sign}${m}m`;
}

export function SecondaryPortCalculator() {
  // Standard-port HW + LW
  const [stdHwTime, setStdHwTime] = useState("09:34");
  const [stdHwHeight, setStdHwHeight] = useState("5.4");
  const [stdLwTime, setStdLwTime] = useState("15:42");
  const [stdLwHeight, setStdLwHeight] = useState("1.2");
  // Differences from the Almanac's secondary-port column
  const [hwTimeDiff, setHwTimeDiff] = useState("-15");
  const [lwTimeDiff, setLwTimeDiff] = useState("-20");
  const [hwHeightDiff, setHwHeightDiff] = useState("-0.4");
  const [lwHeightDiff, setLwHeightDiff] = useState("-0.1");
  const [stdName, setStdName] = useState("Dover");
  const [secName, setSecName] = useState("Folkestone");

  const stdHwMin = parseTime(stdHwTime);
  const stdLwMin = parseTime(stdLwTime);
  const stdHwH = Number(stdHwHeight);
  const stdLwH = Number(stdLwHeight);
  const dhwT = Number(hwTimeDiff);
  const dlwT = Number(lwTimeDiff);
  const dhwH = Number(hwHeightDiff);
  const dlwH = Number(lwHeightDiff);

  const valid =
    stdHwMin !== null &&
    stdLwMin !== null &&
    Number.isFinite(stdHwH) &&
    Number.isFinite(stdLwH) &&
    Number.isFinite(dhwT) &&
    Number.isFinite(dlwT) &&
    Number.isFinite(dhwH) &&
    Number.isFinite(dlwH);

  const std: PortPrediction | null = valid
    ? {
        hwTimeMins: stdHwMin!,
        hwHeight: stdHwH,
        lwTimeMins: stdLwMin!,
        lwHeight: stdLwH,
      }
    : null;
  const diff: SecondaryDifferences = {
    hwTimeMins: valid ? dhwT : 0,
    lwTimeMins: valid ? dlwT : 0,
    hwHeight: valid ? dhwH : 0,
    lwHeight: valid ? dlwH : 0,
  };
  const sec = std ? applySecondaryCorrection(std, diff) : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Secondary-port calculator</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Apply the Almanac&apos;s secondary-port time + height differences
        to a Standard Port prediction. Times wrap on a 24-hour clock;
        heights are added directly. The worked steps below match the
        column-by-column form the RYA Day Skipper exam expects.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field
          label="Standard Port name"
          value={stdName}
          onChange={setStdName}
        />
        <Field
          label="Secondary Port name"
          value={secName}
          onChange={setSecName}
        />
      </div>

      <h4 style={{ margin: "10px 0 6px", fontSize: 14 }}>
        Standard Port — {stdName || "(unnamed)"}
      </h4>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <Field label="HW time" value={stdHwTime} onChange={setStdHwTime} hint="HH:MM" />
        <Field label="HW height (m)" value={stdHwHeight} onChange={setStdHwHeight} type="number" />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field label="LW time" value={stdLwTime} onChange={setStdLwTime} hint="HH:MM" />
        <Field label="LW height (m)" value={stdLwHeight} onChange={setStdLwHeight} type="number" />
      </div>

      <h4 style={{ margin: "10px 0 6px", fontSize: 14 }}>
        Differences (from Almanac table)
      </h4>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <Field
          label="HW time diff (min)"
          value={hwTimeDiff}
          onChange={setHwTimeDiff}
          hint="+ later, − earlier"
          type="number"
          step={1}
        />
        <Field
          label="HW height diff (m)"
          value={hwHeightDiff}
          onChange={setHwHeightDiff}
          hint="+ higher, − lower"
          type="number"
        />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field
          label="LW time diff (min)"
          value={lwTimeDiff}
          onChange={setLwTimeDiff}
          hint="+ later, − earlier"
          type="number"
          step={1}
        />
        <Field
          label="LW height diff (m)"
          value={lwHeightDiff}
          onChange={setLwHeightDiff}
          hint="+ higher, − lower"
          type="number"
        />
      </div>

      {sec && valid ? (
        <>
          <div
            style={{
              padding: 10,
              marginBottom: 12,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--accent)",
              background: "var(--accent-soft)",
            }}
          >
            <div
              className="small muted"
              style={{ marginBottom: 4, fontWeight: 600 }}
            >
              Secondary Port — {secName || "(unnamed)"}
            </div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div className="small muted">HW</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {formatTime(sec.hwTimeMins)} · {sec.hwHeight.toFixed(2)} m
                </div>
              </div>
              <div>
                <div className="small muted">LW</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {formatTime(sec.lwTimeMins)} · {sec.lwHeight.toFixed(2)} m
                </div>
              </div>
              <div>
                <div className="small muted">Range</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {(sec.hwHeight - sec.lwHeight).toFixed(2)} m
                </div>
              </div>
            </div>
          </div>

          <div
            className="small"
            style={{
              padding: 12,
              background: "var(--bg-elev)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
{`Working — ${stdName || "Standard"} → ${secName || "Secondary"}
─────────────────────────────────────────────
HW: ${formatTime(stdHwMin!)} · ${stdHwH.toFixed(2)} m
  Time diff = ${fmtMinDiff(dhwT)}     → ${formatTime(sec.hwTimeMins)}
  Height diff = ${dhwH >= 0 ? "+" : ""}${dhwH.toFixed(2)} m → ${sec.hwHeight.toFixed(2)} m

LW: ${formatTime(stdLwMin!)} · ${stdLwH.toFixed(2)} m
  Time diff = ${fmtMinDiff(dlwT)}     → ${formatTime(sec.lwTimeMins)}
  Height diff = ${dlwH >= 0 ? "+" : ""}${dlwH.toFixed(2)} m → ${sec.lwHeight.toFixed(2)} m

Range at ${secName || "secondary port"} = ${(sec.hwHeight - sec.lwHeight).toFixed(2)} m
Duration HW→LW = ${(((sec.lwTimeMins - sec.hwTimeMins + 1440) % 1440) / 60).toFixed(2)} h`}
          </div>

          <p className="small muted" style={{ margin: "10px 0 0" }}>
            <strong>Coastal Skipper note:</strong> the Almanac&apos;s
            differences vary between MHWS / MHWN / MLWS / MLWN columns;
            for the most accurate height at non-springs / non-neaps
            days you interpolate the difference linearly between the
            two relevant columns by the day&apos;s actual range. For
            Day Skipper, the direct-addition form shown here is
            accepted.
          </p>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter the Standard Port prediction + the Almanac differences.
        </p>
      )}
    </div>
  );
}
